import type { WatchSource } from 'vue';
import { computed, proxyRefs, ref, watch } from 'vue';

export type ErrorItem = Readonly<{
  /** Optional. When omitted, the rendering `ErrorsPanel` supplies its page-specific default title. */
  title?: string;
  description: string;
}>;

export type ErrorController = Readonly<{
  errors: ReadonlyArray<ErrorItem>;
  hasErrors: boolean;
  showError: (message: string) => void;
  showErrorItem: (error: ErrorItem) => void;
  dismiss: (index: number) => void;
  clear: () => void;
}>;

type UseErrorControllerProps = {
  readonly clearOn?: WatchSource<unknown>;
};

export const useErrorController = (options?: UseErrorControllerProps): ErrorController => {
  const items = ref<Array<ErrorItem>>([]);
  // Re-read `items.value` per call — capturing `items.value.push` directly would
  // (a) lose the Vue reactivity proxy's `this` binding and (b) freeze the
  // reference, breaking after `clear()` reassigns the underlying array.
  const showErrorItem = (item: ErrorItem): void => {
    items.value.push(item);
  };

  if (options?.clearOn !== undefined) {
    watch(options.clearOn, () => {
      items.value = [];
    });
  }

  return proxyRefs({
    errors: computed<ReadonlyArray<ErrorItem>>(() => items.value),
    hasErrors: computed(() => items.value.length > 0),
    showErrorItem,
    showError: (description: string) => {
      // Title omitted — resolved at render time by the page's ErrorsPanel default.
      showErrorItem({ description });
    },
    dismiss: (index: number) => {
      items.value.splice(index, 1);
    },
    clear: () => {
      items.value = [];
    },
  });
};

/**
 * Auto-clears errors on the `false → true` transition of a boolean source
 * (typically `isSaving` / `isPending`). Use to drop stale errors at the start of
 * a new operation without sprinkling `errors.clear()` calls inside save handlers.
 *
 * Falling-edge transitions (`true → false`) are intentionally ignored — that's
 * the moment errors *get pushed*, so clearing then would erase what the user
 * just received.
 */
export const useResetErrorsOnStart = (errors: ErrorController, source: WatchSource<boolean>): void => {
  watch(source, (current, previous) => {
    if (current && !previous) {
      errors.clear();
    }
  });
};
